import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch,
    faPhone,
    faPersonWalking,
    faClipboardCheck,
    faArrowRight,
    faShieldHalved,
    faCalendarDays,
    faXmark
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import ConfirmationModal from '../../components/ConfirmationModal';
import { useAuth } from '../../contexts/AuthContext';
import ProjectsStatsCards from './ProjectsStatsCards';

const ChefTerrainSuperieurDashboard = () => {
    const navigate = useNavigate();
    // Chef terrain shares this dashboard with his supérieur but is F2F-only: the API already
    // returns no CATI surveys for him, and here we drop the CATI chrome so he isn't shown
    // an empty card and a tab that can never have content.
    const { user } = useAuth();
    const f2fOnly = user?.role === 'chef_terrain';
    const [surveyProgressData, setSurveyProgressData] = useState({ cati: [], f2f: [], clients: [], available_years: [], total_control_surveys: 0 });
    const [surveyProgressLoading, setSurveyProgressLoading] = useState(false);
    const [spClientFilter, setSpClientFilter] = useState('');
    const [spYearFilter, setSpYearFilter] = useState('');
    const [spSearch, setSpSearch] = useState('');
    const [activeTab, setActiveTab] = useState('all');

    useEffect(() => {
        fetchSurveyProgress('', '', '');
    }, []);

    const spFilterMounted = useRef(false);
    useEffect(() => {
        if (!spFilterMounted.current) { spFilterMounted.current = true; return; }
        fetchSurveyProgress(spClientFilter, spYearFilter, spSearch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spClientFilter, spYearFilter]);

    const fetchSurveyProgress = async (clientId, year, search) => {
        try {
            setSurveyProgressLoading(true);
            const params = {};
            if (clientId) params.client_id = clientId;
            if (year) params.year = year;
            if (search && search.trim()) params.search = search.trim();
            const response = await api.get('/cati/surveys/progress', { params });
            if (response.data.success) {
                setSurveyProgressData(prev => ({
                    ...response.data.data,
                    clients: response.data.data.clients || prev.clients,
                    available_years: response.data.data.available_years || prev.available_years,
                }));
            }
        } catch (error) {
            console.error('Error fetching survey progress:', error);
        } finally {
            setSurveyProgressLoading(false);
        }
    };

    // "Terminer": manually hide a survey from every progress dashboard (used when a control was left
    // active). Pure visibility flag — the control is untouched. Reversible via search → Réactiver.
    const [terminateTarget, setTerminateTarget] = useState(null);
    const [terminating, setTerminating] = useState(false);
    // Without this the modal just closed on a 403 and nothing changed, which reads as a dead button.
    const [actionError, setActionError] = useState(null);
    const confirmTerminateSurvey = async () => {
        if (!terminateTarget) return;
        try {
            setTerminating(true);
            setActionError(null);
            await api.post(`/cati/surveys/${terminateTarget.id}/terminate-progress`);
            setTerminateTarget(null);
            fetchSurveyProgress(spClientFilter, spYearFilter, spSearch);
        } catch (error) {
            console.error('Error terminating survey progress:', error);
            setTerminateTarget(null);
            setActionError(error.response?.data?.message || 'Impossible de terminer cette enquête.');
        } finally {
            setTerminating(false);
        }
    };
    // Reverse a "Terminé" survey (surfaced via search) — non-destructive, no confirm needed.
    const reactivateSurvey = async (survey) => {
        try {
            setActionError(null);
            await api.post(`/cati/surveys/${survey.id}/reactivate-progress`);
            fetchSurveyProgress(spClientFilter, spYearFilter, spSearch);
        } catch (error) {
            console.error('Error reactivating survey progress:', error);
            setActionError(error.response?.data?.message || 'Impossible de réactiver cette enquête.');
        }
    };

    const totalCati = surveyProgressData.cati.length;
    const totalF2f = surveyProgressData.f2f.length;
    const totalControlled = surveyProgressData.total_control_surveys || 0;



    const hasFilters = spClientFilter || spYearFilter || spSearch;

    // Filter surveys by active tab
    const getFilteredSurveys = () => {
        if (activeTab === 'cati') return { cati: surveyProgressData.cati, f2f: [] };
        if (activeTab === 'f2f') return { cati: [], f2f: surveyProgressData.f2f };
        return { cati: surveyProgressData.cati, f2f: surveyProgressData.f2f };
    };
    const filtered = getFilteredSurveys();

    const renderSurveyCard = (survey, type) => {
        const completionPct = survey.general_target ? Math.min(100, Math.round((survey.completions / survey.general_target) * 100)) : null;
        const controlPct = survey.general_target > 0 ? Math.min(100, Math.round((survey.controls_done / survey.general_target) * 100)) : 0;
        const controlTargetPct = survey.control_percentage ? Math.min(100, parseFloat(survey.control_percentage)) : null;
        const humanCount = survey.controls_done_human ?? survey.controls_done ?? 0;
        const aiOnlyCount = survey.controls_done_ai_only ?? 0;
        const humanWidth = survey.general_target > 0 ? Math.min(100, (humanCount / survey.general_target) * 100) : 0;
        const aiWidth = survey.general_target > 0 ? Math.min(100 - humanWidth, (aiOnlyCount / survey.general_target) * 100) : 0;
        const totalDoneCount = humanCount + aiOnlyCount;
        const humanPct = totalDoneCount > 0 ? Math.round((humanCount / totalDoneCount) * 100) : 0;
        const aiPct = totalDoneCount > 0 ? Math.round((aiOnlyCount / totalDoneCount) * 100) : 0;
        const budgetPct = survey.has_budget && survey.total_budget > 0 ? Math.min(100, Math.round((survey.budget_spent / survey.total_budget) * 1000) / 10) : null;
        const isCati = type === 'cati';

        return (
            <div
                key={survey.id}
                className="group bg-white rounded-xl border border-gray-200 p-4 sm:p-5 cursor-pointer hover:shadow-lg hover:border-gray-300 hover:-translate-y-0.5 transition-all duration-200"
                onClick={() => navigate(`/cati/surveys/${survey.id}/stats`)}
            >
                {/* Header */}
                <div className="mb-3">
                    <div className="flex items-start justify-between gap-2">
                        <h4 className="text-sm font-semibold text-gray-900 leading-tight break-words group-hover:text-red-700 transition-colors min-w-0">
                            {survey.title}
                        </h4>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
                                {isCati ? 'CATI' : 'F2F'}
                            </span>
                            {survey.progress_terminated_at ? (
                                <button
                                    onClick={(e) => { e.stopPropagation(); reactivateSurvey(survey); }}
                                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 transition-colors"
                                    title="Réafficher cette enquête dans les tableaux de bord"
                                >
                                    Réactiver
                                </button>
                            ) : (
                                <button
                                    onClick={(e) => { e.stopPropagation(); setTerminateTarget(survey); }}
                                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors"
                                    title="Retirer cette enquête des tableaux de bord (le contrôle reste inchangé)"
                                >
                                    Terminer
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {survey.client_name && (
                            <span className="text-xs text-gray-500">{survey.client_name}</span>
                        )}
                        <span className="text-[10px] text-gray-400 flex items-center gap-1">
                            <FontAwesomeIcon icon={faCalendarDays} className="text-[9px]" />
                            {survey.date_debut} - {survey.date_fin}
                        </span>
                    </div>
                </div>

                {/* Progress bars */}
                <div className="space-y-3">
                    {/* Completion */}
                    <div>
                        <div className="flex items-center justify-between text-[11px] sm:text-xs mb-1.5">
                            <span className="text-gray-500">
                                Complétions
                            </span>
                            <span className="font-semibold text-gray-700 tabular-nums">
                                {survey.completions.toLocaleString('fr-FR')}
                                {survey.general_target ? <span className="text-gray-400 font-normal"> / {survey.general_target.toLocaleString('fr-FR')}</span> : ''}
                                {completionPct !== null && <span className="ml-1 sm:ml-1.5 text-gray-400 font-normal">({completionPct}%)</span>}
                            </span>
                        </div>
                        {survey.general_target > 0 && (
                            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                                <div
                                    className="h-2 rounded-full transition-all duration-700 ease-out"
                                    style={{
                                        width: `${completionPct}%`,
                                        background: completionPct >= 80
                                            ? 'linear-gradient(90deg, #22c55e, #16a34a)'
                                            : completionPct >= 50
                                            ? 'linear-gradient(90deg, #facc15, #eab308)'
                                            : 'linear-gradient(90deg, #f87171, #dc2626)'
                                    }}
                                ></div>
                            </div>
                        )}
                    </div>

                    {/* Control */}
                    <div>
                        <div className="flex items-center justify-between text-[11px] sm:text-xs mb-1.5">
                            <span className="text-gray-500 flex-shrink-0">
                                Contrôle
                                {controlTargetPct != null && (
                                    <span className="text-gray-400 font-normal hidden sm:inline"> (objectif {controlTargetPct}%)</span>
                                )}
                            </span>
                            <span className="font-semibold text-gray-700 tabular-nums text-right">
                                {survey.controls_done.toLocaleString('fr-FR')}
                                <span className="text-gray-400 font-normal"> / {survey.general_target ? survey.general_target.toLocaleString('fr-FR') : '—'}</span>
                                {survey.general_target > 0 && <span className="ml-1 sm:ml-1.5 text-gray-400 font-normal">({controlPct}%)</span>}
                            </span>
                        </div>
                        {survey.general_target > 0 && (
                            <div className="relative w-full bg-gray-100 rounded-full h-2 flex overflow-hidden">
                                <div
                                    className="h-2 transition-all duration-700 ease-out"
                                    style={{
                                        width: `${humanWidth}%`,
                                        background: controlTargetPct != null && controlPct >= controlTargetPct && aiOnlyCount === 0
                                            ? 'linear-gradient(90deg, #22c55e, #16a34a)'
                                            : 'linear-gradient(90deg, #60a5fa, #3b82f6)'
                                    }}
                                    title={`Humain: ${humanCount} (${humanPct}%)`}
                                ></div>
                                <div
                                    className="h-2 transition-all duration-700 ease-out"
                                    style={{
                                        width: `${aiWidth}%`,
                                        background: 'linear-gradient(90deg, #818cf8, #6366f1)'
                                    }}
                                    title={`IA: ${aiOnlyCount} (${aiPct}%)`}
                                ></div>
                                {controlTargetPct != null && controlTargetPct > 0 && (
                                    <div className="absolute top-1/2 -translate-y-1/2 pointer-events-none" style={{ left: `${controlTargetPct}%` }}>
                                        <div className="w-0.5 h-4 bg-red-500 rounded-full -ml-px"></div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {survey.has_budget ? (
                        <div>
                            <div className="flex items-center justify-between text-[11px] sm:text-xs mb-1.5">
                                <span className="text-gray-500">Budget</span>
                                <span className="font-semibold text-gray-700 tabular-nums text-right">
                                    {Number(survey.budget_spent).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                    <span className="text-gray-400 font-normal"> / {Number(survey.total_budget).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD</span>
                                    {budgetPct !== null && <span className="ml-1 sm:ml-1.5 text-gray-400 font-normal">({budgetPct}%)</span>}
                                </span>
                            </div>
                            {budgetPct !== null && (
                                <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                                    <div className="h-2 rounded-full transition-all duration-700 ease-out" style={{
                                        width: `${budgetPct}%`,
                                        background: budgetPct >= 90 ? 'linear-gradient(90deg, #f87171, #dc2626)' : budgetPct >= 70 ? 'linear-gradient(90deg, #facc15, #eab308)' : 'linear-gradient(90deg, #22c55e, #16a34a)'
                                    }}></div>
                                </div>
                            )}
                            {/* Category breakdown */}
                            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
                                {survey.budget_enqueteur > 0 && (
                                    <div className="flex justify-between"><span className="text-gray-400">Enquêteurs</span><span className="text-gray-600 tabular-nums">{Number(survey.spent_enqueteur).toLocaleString('fr-FR')} / {Number(survey.budget_enqueteur).toLocaleString('fr-FR')}</span></div>
                                )}
                                {survey.budget_controle > 0 && (
                                    <div className="flex justify-between"><span className="text-gray-400">Contrôle</span><span className="text-gray-600 tabular-nums">{Number(survey.spent_controle).toLocaleString('fr-FR')} / {Number(survey.budget_controle).toLocaleString('fr-FR')}</span></div>
                                )}
                                {survey.budget_deplacement > 0 && (
                                    <div className="flex justify-between"><span className="text-gray-400">Déplacement</span><span className="text-gray-600 tabular-nums">{Number(survey.spent_deplacement).toLocaleString('fr-FR')} / {Number(survey.budget_deplacement).toLocaleString('fr-FR')}</span></div>
                                )}
                                {survey.budget_imprevus > 0 && (
                                    <div className="flex justify-between"><span className="text-gray-400">Imprévus</span><span className={`tabular-nums ${survey.imprevus_consumed > survey.budget_imprevus ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>{Number(survey.imprevus_consumed).toLocaleString('fr-FR')} / {Number(survey.budget_imprevus).toLocaleString('fr-FR')}</span></div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="text-[11px] text-gray-400 italic">Budget non encore défini</div>
                    )}
                </div>

                {/* Footer hint */}
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-end">
                    <span className="text-[11px] text-gray-400 group-hover:text-red-500 transition-colors flex items-center gap-1">
                        Voir détails <FontAwesomeIcon icon={faArrowRight} className="text-[9px]" />
                    </span>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
            <div className="p-4 sm:p-6 lg:p-8">
                <div className="max-w-7xl mx-auto">

                    {/* Header */}
                    <div className="mb-8">
                        <div className="mb-2">
                            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Tableau de Bord</h1>
                            <p className="text-xs sm:text-sm text-gray-500">Head of Operations</p>
                        </div>
                    </div>

                    {actionError && (
                        <div className="mb-4 flex items-start justify-between gap-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
                            <span>{actionError}</span>
                            <button type="button" onClick={() => setActionError(null)} className="text-red-400 hover:text-red-600" title="Fermer">
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                    )}

                    {/* Rien pour chef_terrain : le composant filtre lui-même par rôle, et
                        chef_terrain n'a pas accès aux projets. */}
                    <ProjectsStatsCards />

                    {/* KPI Cards */}
                    <div className={`grid grid-cols-2 ${f2fOnly ? 'sm:grid-cols-2' : 'sm:grid-cols-3'} gap-3 sm:gap-4 mb-6`}>
                        {/* CATI Projects */}
                        {!f2fOnly && (
                        <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-5 hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between mb-2 sm:mb-3">
                                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gray-100 flex items-center justify-center">
                                    <FontAwesomeIcon icon={faPhone} className="text-gray-600 text-xs sm:text-sm" />
                                </div>
                                <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-semibold bg-gray-100 text-gray-600 uppercase tracking-wide">CATI</span>
                            </div>
                            <div className="text-xl sm:text-3xl font-bold text-gray-900 tracking-tight">{totalCati}</div>
                            <div className="text-[10px] sm:text-xs text-gray-500 mt-0.5">Enquêtes en cours</div>
                        </div>
                        )}

                        {/* F2F Projects */}
                        <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-5 hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between mb-2 sm:mb-3">
                                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gray-100 flex items-center justify-center">
                                    <FontAwesomeIcon icon={faPersonWalking} className="text-gray-600 text-xs sm:text-sm" />
                                </div>
                                <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-semibold bg-gray-100 text-gray-600 uppercase tracking-wide">F2F</span>
                            </div>
                            <div className="text-xl sm:text-3xl font-bold text-gray-900 tracking-tight">{totalF2f}</div>
                            <div className="text-[10px] sm:text-xs text-gray-500 mt-0.5">Enquêtes en cours</div>
                        </div>

                        {/* Surveys with active control */}
                        <div className="col-span-2 sm:col-span-1 bg-white rounded-xl border border-gray-200 p-3 sm:p-5 hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between mb-2 sm:mb-3">
                                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gray-100 flex items-center justify-center">
                                    <FontAwesomeIcon icon={faShieldHalved} className="text-gray-600 text-xs sm:text-sm" />
                                </div>
                                <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-semibold bg-gray-100 text-gray-600 uppercase tracking-wide">Contrôle</span>
                            </div>
                            <div className="text-xl sm:text-3xl font-bold text-gray-900 tracking-tight">{totalControlled}</div>
                            <div className="text-[10px] sm:text-xs text-gray-500 mt-0.5">Enquêtes en contrôle actif</div>
                        </div>
                    </div>

                    {/* Survey Progress Section */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                        {/* Section Header + Tabs */}
                        <div className="px-4 sm:px-6 pt-5 pb-0">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                                    Progression des Enquêtes
                                </h3>
                                {/* No tabs for chef terrain: with CATI out of scope all three would
                                    show the same F2F list. */}
                                {!f2fOnly && (
                                <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                                    {[
                                        { key: 'all', label: 'Tous', count: totalCati + totalF2f },
                                        { key: 'cati', label: 'CATI', count: totalCati },
                                        { key: 'f2f', label: 'F2F', count: totalF2f },
                                    ].map(tab => (
                                        <button
                                            key={tab.key}
                                            onClick={() => setActiveTab(tab.key)}
                                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                                                activeTab === tab.key
                                                    ? 'bg-white text-gray-900 shadow-sm'
                                                    : 'text-gray-500 hover:text-gray-700'
                                            }`}
                                        >
                                            {tab.label} <span className="text-gray-400 ml-0.5">({tab.count})</span>
                                        </button>
                                    ))}
                                </div>
                                )}
                            </div>

                            {/* Filters */}
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-2.5 pb-4 border-b border-gray-100">
                                <div className="relative flex-1">
                                    <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                                    <input
                                        type="text"
                                        placeholder="Rechercher par titre, ID ou client..."
                                        value={spSearch}
                                        onChange={(e) => setSpSearch(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && fetchSurveyProgress(spClientFilter, spYearFilter, spSearch)}
                                        className="w-full pl-8 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 focus:bg-white transition-all"
                                    />
                                </div>
                                <select
                                    value={spClientFilter}
                                    onChange={(e) => setSpClientFilter(e.target.value)}
                                    className="px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 focus:bg-white transition-all"
                                >
                                    <option value="">Tous les clients</option>
                                    {surveyProgressData.clients.map(c => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                                <select
                                    value={spYearFilter}
                                    onChange={(e) => setSpYearFilter(e.target.value)}
                                    className="px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 focus:bg-white transition-all"
                                >
                                    <option value="">Toutes les années</option>
                                    {surveyProgressData.available_years.map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                                <button
                                    onClick={() => fetchSurveyProgress(spClientFilter, spYearFilter, spSearch)}
                                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 active:scale-95 transition-all flex-shrink-0 shadow-sm"
                                >
                                    <FontAwesomeIcon icon={faSearch} />
                                </button>
                                {hasFilters && (
                                    <button
                                        onClick={() => { setSpClientFilter(''); setSpYearFilter(''); setSpSearch(''); fetchSurveyProgress('', '', ''); }}
                                        className="px-3 py-2 text-sm text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200 active:scale-95 transition-all flex-shrink-0"
                                    >
                                        <FontAwesomeIcon icon={faXmark} className="mr-1" />
                                        Effacer
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Survey List */}
                        <div className="px-4 sm:px-6 py-5">
                            {surveyProgressLoading ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <div className="w-8 h-8 border-2 border-red-200 border-t-red-600 rounded-full animate-spin"></div>
                                    <p className="mt-3 text-sm text-gray-400">Chargement...</p>
                                </div>
                            ) : (filtered.cati.length === 0 && filtered.f2f.length === 0) ? (
                                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                                    <FontAwesomeIcon icon={faClipboardCheck} className="text-3xl mb-3 text-gray-300" />
                                    <p className="text-sm font-medium text-gray-500">Aucune enquête trouvée</p>
                                    {!hasFilters && (
                                        <p className="text-xs mt-1">Les enquêtes actives avec des dates incluant aujourd'hui apparaîtront ici</p>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {/* CATI Section */}
                                    {filtered.cati.length > 0 && (
                                        <div>
                                            {activeTab === 'all' && (
                                                <div className="flex items-center gap-2 mb-3">
                                                    <div className="w-5 h-5 rounded bg-gray-100 flex items-center justify-center">
                                                        <FontAwesomeIcon icon={faPhone} className="text-gray-600 text-[9px]" />
                                                    </div>
                                                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">CATI</h4>
                                                    <div className="flex-1 h-px bg-gray-100"></div>
                                                    <span className="text-xs text-gray-400">{filtered.cati.length}</span>
                                                </div>
                                            )}
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                                {filtered.cati.map(survey => renderSurveyCard(survey, 'cati'))}
                                            </div>
                                        </div>
                                    )}

                                    {/* F2F Section */}
                                    {filtered.f2f.length > 0 && (
                                        <div>
                                            {activeTab === 'all' && (
                                                <div className="flex items-center gap-2 mb-3">
                                                    <div className="w-5 h-5 rounded bg-gray-100 flex items-center justify-center">
                                                        <FontAwesomeIcon icon={faPersonWalking} className="text-gray-600 text-[9px]" />
                                                    </div>
                                                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Face à Face</h4>
                                                    <div className="flex-1 h-px bg-gray-100"></div>
                                                    <span className="text-xs text-gray-400">{filtered.f2f.length}</span>
                                                </div>
                                            )}
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                                {filtered.f2f.map(survey => renderSurveyCard(survey, 'f2f'))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>
            <ConfirmationModal
                isOpen={!!terminateTarget}
                onClose={() => setTerminateTarget(null)}
                onConfirm={confirmTerminateSurvey}
                title="Terminer l'enquête ?"
                message={terminateTarget ? `« ${terminateTarget.title} » sera retirée de tous les tableaux de bord, même si son contrôle est encore actif. Vous pourrez la réactiver en la recherchant.` : ''}
                confirmText="Terminer"
                loading={terminating}
            />
        </div>
    );
};

export default ChefTerrainSuperieurDashboard;
